import { apiFetch } from './api';
import { authHeaders } from './auth';
import { buildZip, fetchFileBytes, type ExportManifest, type ExportProgress } from './zipBuilder';

export type { ExportProgress } from './zipBuilder';

const saveBlob = (blob: Blob, filename: string) => {
  const objectURL = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectURL;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectURL);
};

// Photo URLs are either presigned R2 URLs or (local dev) same-origin proxy
// URLs. Never send the auth header: R2's CORS policy doesn't allow it, and the
// signature in the URL is the authorization.
const fetchPhoto = (url: string, init?: RequestInit) => fetch(url, init);

/**
 * Exports photos from a batch: asks the server which photos and filenames,
 * downloads the originals directly from storage, zips them in the browser
 * (or saves the single photo as-is) and saves the result.
 *
 * Throws an Error whose message is ready to show the user.
 */
export async function downloadExport(
  path: string,
  fallbackFilename: string,
  onProgress: (progress: ExportProgress) => void,
): Promise<void> {
  let res: Response;
  try {
    res = await apiFetch(path, { headers: authHeaders() });
  } catch (err) {
    throw new Error(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
  }
  if (!res.ok) {
    const body: { message?: string } = await res.json().catch(() => ({}));
    throw new Error(body.message || `Export failed (${res.status})`);
  }
  const manifest: ExportManifest = await res.json();
  if (!manifest.files?.length) throw new Error('No matching photos found');

  try {
    if (manifest.files.length === 1) {
      const [file] = manifest.files;
      let received = 0;
      onProgress({ filesDone: 0, filesTotal: 1, received, total: file.size });
      const bytes = await fetchFileBytes(file, fetchPhoto, (delta) => {
        received += delta;
        onProgress({ filesDone: 0, filesTotal: 1, received, total: file.size });
      });
      onProgress({ filesDone: 1, filesTotal: 1, received, total: file.size });
      saveBlob(new Blob([bytes as BlobPart], { type: 'image/jpeg' }), file.filename);
      return;
    }
    const zip = await buildZip(manifest, fetchPhoto, onProgress);
    saveBlob(zip, manifest.zipName || fallbackFilename);
  } catch (err) {
    throw new Error(`Export failed: ${err instanceof Error ? err.message : String(err)}`);
  }
}
